import type { VehicleFeatures } from 'Pages/vehicles/helpers/vehicle.schema';
import { formatText } from 'Utilities/formatters';

import {
	DEFAULT_FEATURE_ICON,
	MAX_VISIBLE_FEATURES_ADDITIONAL,
	MAX_VISIBLE_FEATURES_STANDARD,
	STANDARD_FEATURES,
	STANDARD_FEATURES_ORDER,
} from './FeaturesList.const';
import type {
	CombinedStandardEquipmentAndNonStandardHighValueFeatures,
	GetFeaturesByVisibilityParams,
	GetFeaturesParams,
	NonStandardHighValueFeature,
	StandardEquipmentFeature,
} from './FeaturesList.helpers.types';

export const sortEquipment = (a: string, b: string) =>
	STANDARD_FEATURES_ORDER.indexOf(a) - STANDARD_FEATURES_ORDER.indexOf(b);

export const getNonStandardFeature = ({ name, price }: VehicleFeatures['highValueCarFeatures']['items'][number]) => ({
	icon: DEFAULT_FEATURE_ICON,
	label: name,
	subtext: `£${formatText(price)}`,
});

export const getNonStandardFeatures = (
	features: VehicleFeatures['highValueCarFeatures']['items'] = [],
): NonStandardHighValueFeature[] =>
	features.filter(({ price }) => Number(price) !== 0).map((feature) => getNonStandardFeature(feature));

export const getStandardFeature = (feature: string) =>
	Object.values(STANDARD_FEATURES).find((standardFeature) => standardFeature.key === feature);

export const getStandardFeatures = (features: string[] = []): (StandardEquipmentFeature | undefined)[] =>
	features.map((feature) => getStandardFeature(feature));

export const getVisibleFeatures = (
	limit: number,
	features: CombinedStandardEquipmentAndNonStandardHighValueFeatures = [],
) => features.slice(0, limit);

export const getHiddenFeatures = (
	limit: number,
	features: CombinedStandardEquipmentAndNonStandardHighValueFeatures = [],
) => (features.length > limit ? features.slice(limit, features.length) : []);

export const getFeaturesByVisibility = ({
	equipmentItems,
	highValueCarFeaturesItems,
}: GetFeaturesByVisibilityParams) => {
	const additionalSpecV2ItemsByPriceDesc = highValueCarFeaturesItems.sort((a, b) => b.price - a.price);
	const sortedEquipment = equipmentItems.sort(sortEquipment);

	// Type guard needed as filter Boolean doesn't narrow type in our version of Typescript
	const standardFeatures = getStandardFeatures(sortedEquipment)?.filter(
		(standardFeature): standardFeature is StandardEquipmentFeature => !!standardFeature,
	);
	const nonStandardFeatures = getNonStandardFeatures(additionalSpecV2ItemsByPriceDesc)?.filter(
		(nonStandardFeature): nonStandardFeature is NonStandardHighValueFeature => !!nonStandardFeature,
	);

	const orderedFeatureList = [...nonStandardFeatures, ...standardFeatures];

	const limit = nonStandardFeatures.length === 0 ? MAX_VISIBLE_FEATURES_STANDARD : MAX_VISIBLE_FEATURES_ADDITIONAL;

	const visibleFeatures = getVisibleFeatures(limit, orderedFeatureList);
	const hiddenFeatures = getHiddenFeatures(limit, orderedFeatureList);

	return {
		hiddenFeatures,
		visibleFeatures,
	};
};

export const getFeatures = ({ equipmentItems, highValueCarFeaturesItems }: GetFeaturesParams) => {
	const { hiddenFeatures, visibleFeatures } = getFeaturesByVisibility({
		equipmentItems,
		highValueCarFeaturesItems,
	});
	const featuresCount = hiddenFeatures.length + visibleFeatures.length;
	return {
		featuresCount,
		hiddenFeatures,
		visibleFeatures,
	};
};
